<template>
  <div
    class="engine-empty-state text-center my-12"
    :class="`engine-empty-state--${variant}`"
    :data-engine="engine"
    :data-variant="variant"
  >
    <v-icon large :color="presentation.color">{{ presentation.icon }}</v-icon>
    <p class="mt-4 mb-1 engine-empty-state__title">{{ presentation.title }}</p>
    <p
      v-if="presentation.detail"
      class="caption grey--text text--darken-1 mb-3 engine-empty-state__detail"
    >
      {{ presentation.detail }}
    </p>
    <v-btn
      v-if="variant === 'skipped' && canLaunch"
      small
      outlined
      color="primary"
      class="engine-empty-state__launch"
      @click="$emit('launch', { engine })"
    >
      <v-icon small left>mdi-play-circle-outline</v-icon>
      Launch with this engine
    </v-btn>
  </div>
</template>

<script>
/** Human reasons for the skip codes emitted by the orchestrator (contract 3). */
export const SKIP_REASON_TEXT = Object.freeze({
  not_requested: 'The engine was switched off when this run was launched.',
  no_list_component: 'The interview has no list component (ideal / applicant order).',
  pre_competency_missing: 'Pre-competency did not run, so this engine ran degraded.',
});

/**
 * Empty block for an analysis tab (M3 task F8). Three variants:
 *   - `skipped`: 'Not run in this run (engine off)' with a 'Launch with this engine' button;
 *   - `failed`:  'Failed at stage <stage>' when the run ended in error before the engine ran;
 *   - `empty`:   plain 'No <label> data available.' (legacy docs, missing payload).
 * Callers pass the engine name and label plus the run info from `analysisData/getEnginesRun`;
 * the variant is derived unless forced through `variant`.
 */
export default {
  name: 'EngineEmptyState',

  props: {
    /** Catalogue engine name ('coherence', 'cultural_fit', ...). */
    engine: {
      type: String,
      required: true,
    },
    /** Lower-case label used in sentences ('cultural fit'). */
    label: {
      type: String,
      default: '',
    },
    /** Skip reason of this engine in the viewed run (null when it ran). */
    skipReason: {
      type: String,
      default: null,
    },
    /** Ledger status of the viewed run ('FAILED' triggers the failed variant). */
    runStatus: {
      type: String,
      default: null,
    },
    /** Run error block {type, message, stage} when the run failed. */
    runError: {
      type: Object,
      default: null,
    },
    /** Force a variant instead of deriving it. */
    forcedVariant: {
      type: String,
      default: null,
      validator: (value) => value == null || ['skipped', 'failed', 'empty'].includes(value),
    },
    /** Show the launch button on the skipped variant. */
    canLaunch: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    variant() {
      if (this.forcedVariant) return this.forcedVariant;
      if (this.skipReason) return 'skipped';
      if (String(this.runStatus || '').toUpperCase() === 'FAILED') return 'failed';
      return 'empty';
    },
    labelText() {
      return this.label || this.engine.replace(/_/g, ' ');
    },
    presentation() {
      if (this.variant === 'skipped') {
        return {
          icon: 'mdi-power-plug-off-outline',
          color: 'grey',
          title: 'Not run in this run (engine off)',
          detail: SKIP_REASON_TEXT[this.skipReason] || `Skip reason: ${this.skipReason}`,
        };
      }
      if (this.variant === 'failed') {
        const stage = this.runError?.stage || null;
        const message = this.runError?.message || '';
        return {
          icon: 'mdi-alert-circle-outline',
          color: 'error',
          title: stage ? `Failed at stage ${stage}` : 'The run failed before this engine ran',
          detail: message,
        };
      }
      return {
        icon: 'mdi-information',
        color: 'grey',
        title: `No ${this.labelText} data available.`,
        detail: '',
      };
    },
  },
};
</script>

<style scoped>
.engine-empty-state__title {
  font-size: 16px;
}
</style>
